/**
 * SensorFormulaProcessor
 *
 * Comprehensive processor for sensor formula calculations.
 * Supports all rule types from SensorFormulaCalculator component:
 * - Conditions (10 types)
 * - Operations (20 types)
 * - Formatting (8 types)
 *
 * @version 2.1
 */

import { evaluateTemporalCondition, parseFormulaTimestamp } from './formulaDates';

class SensorFormulaProcessor {
  constructor() {
    this.reset();
  }

  /**
   * Reset the processor state (call when starting a new data series)
   */
  reset() {
    this.state = {
      // For operations that need previous INPUT value (delta, percentChange, etc.)
      previousInputValue: null,

      // For moving average - stores previous INPUT values
      movingAvgBuffer: [],

      // For cumulative sum
      cumulativeSum: 0,

      // For deadband - stores last OUTPUT value that passed the threshold
      deadbandLastOutput: null,

      // For consecutiveCount condition
      consecutiveCount: 0,
      lastConditionMet: false,
    };
  }

  /**
   * Parse the formula configuration
   * Supports both new JSON format and legacy format
   */
  parseConfig(calcForm) {
    if (calcForm == null || calcForm === '') {
      return null;
    }

    if (typeof calcForm === 'object') {
      return calcForm;
    }

    try {
      const parsed = JSON.parse(calcForm);
      if (parsed && parsed.rules) {
        return parsed;
      }
    } catch (e) {
      // Not JSON, try legacy format
    }

    return this.parseLegacyFormat(calcForm);
  }

  /**
   * Parse legacy format into new structure
   */
  parseLegacyFormat(val) {
    if (typeof val !== 'string') return null;

    const parts = val.split('!').filter(v => v !== '');
    const rules = [];

    for (let i = 0; i < parts.length - 1; i += 2) {
      const op = parts[i];
      const value = parts[i + 1];

      if (['>', '>=', '<', '<='].some(c => op.includes(c))) {
        let source = 'raw';
        let operator = op;
        let showOnly = false;

        if (op.startsWith('sh')) {
          showOnly = true;
          operator = op.substring(2);
        } else if (op.startsWith('f')) {
          source = 'final';
          operator = op.substring(1);
        }

        rules.push({
          category: 'conditions',
          type: 'valueCompare',
          enabled: true,
          source,
          operator,
          value: parseFloat(value),
          showOnly
        });
      } else if (['+', '-', '*', '/'].includes(op)) {
        const typeMap = { '+': 'add', '-': 'subtract', '*': 'multiply', '/': 'divide' };
        rules.push({
          category: 'operations',
          type: typeMap[op],
          enabled: true,
          value: parseFloat(value)
        });
      } else if (op === 'b+') {
        rules.push({
          category: 'operations',
          type: 'addPrevious',
          enabled: true
        });
      } else if (op === 'b-') {
        rules.push({
          category: 'operations',
          type: 'subtractPrevious',
          enabled: true
        });
      }
    }

    return { version: 2, rules };
  }

  /**
   * Process a single data point through the formula
   */
  processValue({ value, timestamp, previousData = [], config }) {
    const inputValue = value; // Store original input

    if (!config || !config.rules || config.rules.length === 0) {
      this.state.previousInputValue = inputValue;
      return {
        value: value,
        show: true,
        formatted: this.formatNumber(value),
        alerts: []
      };
    }

    let finalValue = value;
    let allowCalc = true;
    let show = true;
    const formattingRules = [];

    // Separate rules by category
    const conditions = config.rules.filter(r => r.category === 'conditions' && r.enabled);
    const operations = config.rules.filter(r => r.category === 'operations' && r.enabled);
    const formatting = config.rules.filter(r => r.category === 'formatting' && r.enabled);

    // Process conditions
    for (const rule of conditions) {
      const conditionResult = this.evaluateCondition(rule, inputValue, finalValue, timestamp, previousData);

      if (rule.showOnly) {
        if (!conditionResult) {
          show = false;
        }
      } else {
        allowCalc = allowCalc && conditionResult;
      }
    }

    // Process operations if conditions allow
    if (allowCalc) {
      for (const rule of operations) {
        finalValue = this.applyOperation(rule, inputValue, finalValue, previousData);
      }
    }

    // Collect formatting rules
    formattingRules.push(...formatting);

    // Update state AFTER all operations, using INPUT value for next iteration
    this.state.previousInputValue = inputValue;

    // Apply formatting
    const formatted = this.applyFormatting(finalValue, formattingRules);

    return {
      value: finalValue,
      show,
      formatted,
      alerts: [],
      rawValue: inputValue
    };
  }

  /**
   * Evaluate a condition rule
   */
  evaluateCondition(rule, inputValue, currentValue, timestamp, previousData) {
    const sourceValue = rule.source === 'final' ? currentValue : inputValue;
    const date = parseFormulaTimestamp(timestamp);

    switch (rule.type) {
      case 'dateCompare':
      case 'dateTimeCompare':
      case 'timeCompare':
        return evaluateTemporalCondition(rule, timestamp);
      case 'valueCompare':
        return this.compareValues(sourceValue, rule.operator, rule.value);

      case 'range':
        return sourceValue >= rule.min && sourceValue <= rule.max;

      case 'isNull':
        return sourceValue === null || sourceValue === undefined || Number.isNaN(sourceValue);

      case 'isValid':
        return sourceValue !== null && sourceValue !== undefined && !Number.isNaN(sourceValue) && Number.isFinite(sourceValue);

      case 'timeRange': {
        const timeStr = date.toTimeString().slice(0, 5);
        const start = rule.startTime;
        const end = rule.endTime;

        if (start <= end) {
          return timeStr >= start && timeStr <= end;
        } else {
          return timeStr >= start || timeStr <= end;
        }
      }

      case 'dayOfWeek': {
        const dayOfWeek = date.getDay();
        return rule.days.includes(dayOfWeek);
      }

      case 'deltaCompare': {
        if (this.state.previousInputValue === null) return true;
        const delta = sourceValue - this.state.previousInputValue;
        return this.compareValues(delta, rule.operator, rule.value);
      }

      case 'percentChange': {
        if (this.state.previousInputValue === null || this.state.previousInputValue === 0) return true;
        const percentChange = ((sourceValue - this.state.previousInputValue) / Math.abs(this.state.previousInputValue)) * 100;
        return this.compareValues(percentChange, rule.operator, rule.value);
      }

      case 'consecutiveCount': {
        const innerResult = this.compareValues(sourceValue, rule.operator || '>', rule.value);

        if (innerResult === this.state.lastConditionMet && innerResult) {
          this.state.consecutiveCount++;
        } else if (innerResult) {
          this.state.consecutiveCount = 1;
        } else {
          this.state.consecutiveCount = 0;
        }
        this.state.lastConditionMet = innerResult;

        return this.state.consecutiveCount >= (rule.count || 1);
      }

      case 'custom': {
        if (rule.expression) {
          try {
            const v = sourceValue;
            const raw = inputValue;
            const prev = this.state.previousInputValue;
            // eslint-disable-next-line no-eval
            return eval(rule.expression);
          } catch (e) {
            console.error('Custom condition evaluation failed:', e);
            return true;
          }
        }
        return true;
      }

      default:
        return true;
    }
  }

  /**
   * Compare two values with an operator
   */
  compareValues(a, operator, b) {
    switch (operator) {
      case '>': return a > b;
      case '>=': return a >= b;
      case '<': return a < b;
      case '<=': return a <= b;
      case '==': return a == b;
      case '!=': return a != b;
      case '===': return a === b;
      case '!==': return a !== b;
      default: return true;
    }
  }

  /**
   * Apply an operation rule to a value
   * @param {object} rule - The operation rule
   * @param {number} inputValue - Original input value (for delta, etc.)
   * @param {number} currentValue - Current transformed value
   * @param {Array} previousData - Previous data points
   */
  applyOperation(rule, inputValue, currentValue, previousData) {
    switch (rule.type) {
        // ===== BASIC ARITHMETIC =====
      case 'add':
        return currentValue + rule.value;

      case 'subtract':
        return currentValue - rule.value;

      case 'multiply':
        return currentValue * rule.value;

      case 'divide':
        return rule.value !== 0 ? currentValue / rule.value : currentValue;

      case 'power':
        return Math.pow(currentValue, rule.value);

      case 'modulo':
        return rule.value !== 0 ? currentValue % rule.value : currentValue;

        // ===== MATHEMATICAL FUNCTIONS =====
      case 'sqrt':
        return currentValue >= 0 ? Math.sqrt(currentValue) : currentValue;

      case 'abs':
        return Math.abs(currentValue);

      case 'log': {
        if (currentValue <= 0) return currentValue;
        const base = rule.base || 10;
        return base === Math.E ? Math.log(currentValue) : Math.log(currentValue) / Math.log(base);
      }

      case 'negate':
        return -currentValue;

        // ===== ROUNDING =====
      case 'round': {
        const precision = rule.precision !== undefined ? rule.precision : 2;
        const factor = Math.pow(10, precision);
        return Math.round(currentValue * factor) / factor;
      }

      case 'floor':
        return Math.floor(currentValue);

      case 'ceil':
        return Math.ceil(currentValue);

        // ===== RANGE OPERATIONS =====
      case 'clamp':
        return Math.max(rule.min, Math.min(rule.max, currentValue));

      case 'map': {
        const { inMin, inMax, outMin, outMax } = rule;
        if (inMax === inMin) return outMin;
        const normalized = (currentValue - inMin) / (inMax - inMin);
        return outMin + normalized * (outMax - outMin);
      }

        // ===== STATISTICAL / TEMPORAL OPERATIONS =====

        // Moving average: averages the last N INPUT values
      case 'movingAvg': {
        this.state.movingAvgBuffer.push(inputValue);
        const windowSize = rule.window || 5;

        if (this.state.movingAvgBuffer.length > windowSize) {
          this.state.movingAvgBuffer = this.state.movingAvgBuffer.slice(-windowSize);
        }

        const sum = this.state.movingAvgBuffer.reduce((a, b) => a + b, 0);
        return sum / this.state.movingAvgBuffer.length;
      }

        // Deadband: only update output if change exceeds threshold
      case 'deadband': {
        const threshold = rule.threshold || 0;
        if (this.state.deadbandLastOutput === null) {
          this.state.deadbandLastOutput = currentValue;
          return currentValue;
        }
        const diff = Math.abs(currentValue - this.state.deadbandLastOutput);
        if (diff > threshold) {
          this.state.deadbandLastOutput = currentValue;
          return currentValue;
        }
        return this.state.deadbandLastOutput;
      }

        // Previous value: returns the previous INPUT value
      case 'previousValue':
        return this.state.previousInputValue !== null ? this.state.previousInputValue : currentValue;

        // Delta: current INPUT minus previous INPUT
      case 'delta':
        if (this.state.previousInputValue === null) return inputValue;
        return inputValue - this.state.previousInputValue;

        // Cumulative: running sum of INPUT values
      case 'cumulative':
        this.state.cumulativeSum += inputValue;
        return this.state.cumulativeSum;

        // ===== LEGACY OPERATIONS =====
      case 'addPrevious':
        if (previousData.length > 0) {
          return currentValue + previousData[previousData.length - 1].y;
        }
        return currentValue;

      case 'subtractPrevious':
        if (previousData.length > 0) {
          return currentValue - previousData[previousData.length - 1].y;
        }
        return currentValue;

      default:
        return currentValue;
    }
  }

  /**
   * Apply formatting rules to a value
   */
  applyFormatting(value, rules) {
    let text = String(value);
    let decimals = null;
    let prefix = '';
    let suffix = '';
    let thousandSeparator = null;
    let style = {};

    for (const rule of rules) {
      switch (rule.type) {
        case 'prefix':
          prefix = rule.prefix || '';
          break;

        case 'suffix':
          suffix = rule.suffix || '';
          break;

        case 'decimals':
          decimals = rule.decimals !== undefined ? rule.decimals : 2;
          break;

        case 'thousands':
          thousandSeparator = rule.separator || ' ';
          break;

        case 'percentage':
          value = value * 100;
          suffix = suffix || '%';
          break;

        case 'scientific':
          text = value.toExponential(rule.precision || 2);
          break;

        case 'colorize': {
          const { greenThreshold, redThreshold, invertColors } = rule;
          let color;

          if (invertColors) {
            if (value <= greenThreshold) color = '#059669';
            else if (value >= redThreshold) color = '#dc2626';
            else color = '#d97706';
          } else {
            if (value >= greenThreshold) color = '#059669';
            else if (value <= redThreshold) color = '#dc2626';
            else color = '#d97706';
          }

          style.color = color;
          break;
        }

        case 'hideZero':
          if (value === 0) {
            return { text: '', style, hidden: true };
          }
          break;
      }
    }

    // Apply number formatting
    if (decimals !== null) {
      text = value.toFixed(decimals);
    } else if (typeof value === 'number' && !text.includes('e')) {
      text = String(value);
    }

    // Apply thousand separator
    if (thousandSeparator && !text.includes('e')) {
      const parts = text.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
      text = parts.join('.');
    }

    // Apply prefix and suffix
    text = prefix + text + suffix;

    return { text, style, value };
  }

  /**
   * Simple number formatting utility
   */
  formatNumber(value, decimals = 3) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return String(value);
    }
    return value.toFixed(decimals);
  }

  /**
   * Round to specified decimal places
   */
  roundDecimal(value, decimals = 3) {
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
  }
}


// ============================================================================
// MAIN PROCESSING FUNCTION
// ============================================================================

/**
 * Process sensor data through formula calculations
 */
function processSensorData({ mesure, times, values, roundDecimal }) {
  const processor = new SensorFormulaProcessor();
  const config = processor.parseConfig(mesure.calcForm);
  const datas = [];

  const round = roundDecimal || ((v, d) => processor.roundDecimal(v, d));

  for (let i = 0; i < times.length; i++) {
    const time = times[i];
    const value = values[i];

    const result = processor.processValue({
      value,
      timestamp: time,
      previousData: datas,
      config
    });

    if (result.show) {
      const dataPoint = {
        x: time,
        y: round(result.value, 3)
      };

      if (result.formatted && typeof result.formatted === 'object') {
        dataPoint.formatted = result.formatted.text;
        if (result.formatted.style && Object.keys(result.formatted.style).length > 0) {
          dataPoint.style = result.formatted.style;
        }
      }

      datas.push(dataPoint);
    }
  }

  return datas;
}


/**
 * Drop-in replacement for existing code
 */
function processFormulaCalculation(mesure, times, values, roundDecimalFn) {
  return processSensorData({
    mesure,
    times,
    values,
    roundDecimal: roundDecimalFn
  });
}


/**
 * Creates a processor instance for manual loop control
 */
function createFormulaProcessor(mesure) {
  const processor = new SensorFormulaProcessor();
  const config = processor.parseConfig(mesure.calcForm);

  return {
    processor,
    config,

    process(value, time, previousData) {
      return processor.processValue({
        value,
        timestamp: time,
        previousData,
        config
      });
    },

    reset() {
      processor.reset();
    }
  };
}


// ============================================================================
// EXPORTS
// ============================================================================

// SiteForm stores JSON rules; retain the separate legacy multi-measure syntax.
function parseSiteFormula(calcForm) {
  try {
    const config = typeof calcForm === 'string' ? JSON.parse(calcForm) : calcForm;
    return config && Array.isArray(config.rules) ? config : null;
  } catch (e) {
    return null;
  }
}

function applySiteFormula(datasets, config, round = value => value) {
  if (!config) return;
  for (const dataset of datasets) {
    const processor = new SensorFormulaProcessor();
    const history = [];
    for (const point of dataset.data) {
      const result = processor.processValue({
        value: point.y,
        timestamp: point.formulaTimestamp === undefined ? point.x : point.formulaTimestamp,
        previousData: history,
        config
      });
      if (result.show) {
        const output = { ...point, y: round(result.value) };
        delete output.formulaTimestamp;
        history.push(output);
      }
    }
    dataset.data = history;
  }
}

export {
  parseSiteFormula,
  applySiteFormula,
  SensorFormulaProcessor,
  processSensorData,
  processFormulaCalculation,
  createFormulaProcessor
};

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    parseSiteFormula,
    applySiteFormula,
    SensorFormulaProcessor,
    processSensorData,
    processFormulaCalculation,
    createFormulaProcessor
  };
}

if (typeof window !== 'undefined') {
  window.SensorFormulaProcessor = SensorFormulaProcessor;
  window.processSensorData = processSensorData;
  window.processFormulaCalculation = processFormulaCalculation;
  window.createFormulaProcessor = createFormulaProcessor;
}
